import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Save } from "lucide-react";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "react-toastify";

const formSchema = z.object({
  lastName: z.string().min(1, { message: "Le nom est requis." }).nullish(),
  firstName: z.string().min(1, { message: "Le prénom est requis." }).nullish(),
  email: z.string().email({ message: "Doit être un email valide." }).nullish(),
  phoneNumber: z
    .string({ required_error: "Le téléphone est requis." }).min(1, { message: "Le numéro de téléphone est requis." })
    .min(10, { message: "Le téléphone doit comporter au moins 10 chiffres." }).nullish(),
  clinic: z
    .string()
    .min(1, { message: "Le nom de la clinique est requis." })
    .nullish(),
});

const ProfileCard: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      lastName: "",
      firstName: "",
      email: "",
      phoneNumber: "",
      clinic: "",
    },
  });

  useEffect(() => {
    axios
      .get("/practitioner/profile")
      .then((response) => {
        form.reset(response.data);
      })
      .catch((error) => {
        console.error("Error response:", error.response);
        console.error("Erreur lors de la récupération du profil:", error);
        toast.error("Erreur lors de la récupération du profil");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [form]);

  if (loading) {
    return <div>Chargement...</div>;
  }

  function handleSave(data: z.infer<typeof formSchema>) {
    axios
      .put("/practitioner/profile", data)
      .then(() => {
        toast("Profil enregistré", {
          position: "top-center",
          autoClose: 2000,
        });
      })
      .catch((error) => {
        console.error("Error response:", error.response);
        console.error("Erreur lors de la modification du profil:", error);
        toast.error("Erreur lors de la modification du profil", {
          position: "top-center",
          autoClose: 2000,
        });
      });
  }
  return (
    <Card className="w-5/6 min-h-5/6 mt-11 mb-11">
      <CardHeader className="border-b mb-6">
        <CardTitle>Mon profil</CardTitle>
        <CardDescription>Informations sur le praticien</CardDescription>
      </CardHeader>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSave)}>
          <CardContent>
            <div className="flex justify-around mb-6">
              <FormField
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormItem className="w-1/4">
                    <FormLabel>Nom</FormLabel>
                    <FormControl>
                      <Input placeholder="Nom" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormItem className="w-1/4">
                    <FormLabel>Prénom</FormLabel>
                    <FormControl>
                      <Input placeholder="Prenom" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="email"
                render={({ field }) => (
                  <FormItem className="w-1/4">
                    <FormLabel>E-mail</FormLabel>
                    <FormControl>
                      <Input placeholder="nom@mail.fr" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
            <div className="flex justify-around mb-6">
              <FormField
                control={form.control}
                name="phoneNumber"
                render={({ field }) => (
                  <FormItem className="w-1/4">
                    <FormLabel>Téléphone</FormLabel>
                    <FormControl>
                      <Input placeholder="06/07 .. .. .. .." {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={form.control}
                name="clinic"
                render={({ field }) => (
                  <FormItem className="w-1/4">
                    <FormLabel>Etablissement</FormLabel>
                    <FormControl>
                      <Input placeholder="Clinique" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
          <CardFooter className="flex justify-center border-t">
            <Button type="submit" className="mt-6">
              <Save className="mr-2" /> Enregistrer
            </Button>
          </CardFooter>
        </form>
      </Form>
    </Card>
  );
};
export default ProfileCard;
